import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    useDisclaimers,
    useCreateDisclaimer,
    useUpdateDisclaimer,
    useDeleteDisclaimer,
} from '../../hooks/disclaimers';
import { useTestConfigurations } from '../../hooks/testConfigs';
import { useTestMetrics } from '../../hooks/testMetrics';
import type {
    Disclaimer,
    DisclaimerPlacement,
    DisclaimerSeverity,
    DisclaimerStatus,
    DisclaimerTarget,
    CreateDisclaimerDto,
} from '../../types/types';
import { t } from '../../i18n/t';

const STATUSES: DisclaimerStatus[] = ['active', 'inactive'];
const PLACEMENTS: DisclaimerPlacement[] = ['dialog', 'inline', 'card'];
const SEVERITIES: DisclaimerSeverity[] = ['info', 'warning'];

const bodyPreview = (body: string) => {
    const text = body.replace(/\s+/g, ' ').trim();
    return text.length > 80 ? `${text.slice(0, 80)}…` : text;
};

const apiErrorText = (e: unknown, fallback: string): string => {
    if (axios.isAxiosError(e)) {
        const data = e.response?.data as { message?: string | string[] } | string | undefined;
        if (typeof data === 'string' && data.trim()) return data;
        if (data && typeof data === 'object' && data.message) {
            return Array.isArray(data.message) ? data.message.join('\n') : data.message;
        }
    }
    return fallback;
};

const DashboardDisclaimers = () => {
    const { data: disclaimers = [], isLoading, refetch } = useDisclaimers();
    const deleteMutation = useDeleteDisclaimer();
    const [createOpen, setCreateOpen] = useState(false);
    const [editing, setEditing] = useState<Disclaimer | null>(null);

    const handleDelete = async (disclaimer: Disclaimer) => {
        if (!window.confirm(t('disclaimers.deleteConfirm', { code: disclaimer.code }))) return;
        try {
            await deleteMutation.mutateAsync(disclaimer.id);
        } catch (e) {
            console.error(e);
            alert(apiErrorText(e, t('disclaimers.deleteFailed')));
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
                {t('disclaimers.title')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button variant="contained" onClick={() => setCreateOpen(true)}>
                    {t('disclaimers.new')}
                </Button>
                <Tooltip title={t('disclaimers.refresh')}>
                    <IconButton onClick={() => refetch()}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('disclaimers.colCode')}</TableCell>
                            <TableCell>{t('disclaimers.colStatus')}</TableCell>
                            <TableCell>{t('disclaimers.colPlacement')}</TableCell>
                            <TableCell>{t('disclaimers.colSeverity')}</TableCell>
                            <TableCell>{t('disclaimers.colBody')}</TableCell>
                            <TableCell>{t('disclaimers.colTargets')}</TableCell>
                            <TableCell align="right">{t('disclaimers.colActions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {disclaimers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    {t('disclaimers.empty')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            disclaimers.map((disclaimer) => (
                                <TableRow key={disclaimer.id}>
                                    <TableCell>{disclaimer.code}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={t(`disclaimers.status.${disclaimer.status}`)}
                                            color={disclaimer.status === 'active' ? 'success' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>{t(`disclaimers.placement.${disclaimer.placement}`)}</TableCell>
                                    <TableCell>{t(`disclaimers.severity.${disclaimer.severity}`)}</TableCell>
                                    <TableCell>{bodyPreview(disclaimer.body ?? '')}</TableCell>
                                    <TableCell>{disclaimer.targets?.length ?? 0}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title={t('common.edit')}>
                                            <IconButton size="small" onClick={() => setEditing(disclaimer)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={t('common.delete')}>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(disclaimer)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {createOpen && (
                <DisclaimerFormDialog
                    title={t('disclaimers.dialogNew')}
                    onClose={() => setCreateOpen(false)}
                />
            )}
            {editing && (
                <DisclaimerFormDialog
                    title={t('disclaimers.dialogEdit')}
                    disclaimer={editing}
                    onClose={() => setEditing(null)}
                />
            )}
        </Box>
    );
};

interface DisclaimerFormDialogProps {
    title: string;
    disclaimer?: Disclaimer;
    onClose: () => void;
}

const uniqueTargets = (targets: DisclaimerTarget[]): DisclaimerTarget[] => {
    const seen = new Set<string>();
    const result: DisclaimerTarget[] = [];
    for (const target of targets) {
        const key = `${target.type}:${target.targetId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        result.push(target);
    }
    return result;
};

const DisclaimerFormDialog: React.FC<DisclaimerFormDialogProps> = ({
    title,
    disclaimer,
    onClose,
}) => {
    const { data: existing = [] } = useDisclaimers();
    const { data: configurations = [] } = useTestConfigurations();
    const { data: metrics = [] } = useTestMetrics();
    const createMutation = useCreateDisclaimer();
    const updateMutation = useUpdateDisclaimer();
    const isEdit = disclaimer != null;

    const [code, setCode] = useState(disclaimer?.code ?? '');
    const [status, setStatus] = useState<DisclaimerStatus>(disclaimer?.status ?? 'active');
    const [placement, setPlacement] = useState<DisclaimerPlacement>(disclaimer?.placement ?? 'inline');
    const [severity, setSeverity] = useState<DisclaimerSeverity>(disclaimer?.severity ?? 'info');
    const [requireAcknowledgement, setRequireAcknowledgement] = useState(
        disclaimer?.requireAcknowledgement ?? false,
    );
    const [acknowledgementLabel, setAcknowledgementLabel] = useState(
        disclaimer?.acknowledgementLabel ?? '',
    );
    const [titleField, setTitleField] = useState(disclaimer?.title ?? '');
    const [body, setBody] = useState(disclaimer?.body ?? '');
    const [dataJson, setDataJson] = useState(JSON.stringify(disclaimer?.data ?? {}, null, 2));
    const [configTargetIds, setConfigTargetIds] = useState<string[]>(
        () =>
            (disclaimer?.targets ?? [])
                .filter((target) => target.type === 'test_configuration')
                .map((target) => target.targetId),
    );
    const [metricTargetIds, setMetricTargetIds] = useState<string[]>(
        () =>
            (disclaimer?.targets ?? [])
                .filter((target) => target.type === 'test_metric')
                .map((target) => target.targetId),
    );
    const [saving, setSaving] = useState(false);

    const sortedConfigs = useMemo(
        () => [...configurations].sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id),
        [configurations],
    );
    const sortedMetrics = useMemo(
        () => [...metrics].sort((a, b) => a.title.localeCompare(b.title) || a.type.localeCompare(b.type)),
        [metrics],
    );

    const configLabel = (targetId: string) => {
        const config = configurations.find((item) => String(item.id) === targetId);
        return config ? `${config.id} ${config.name}` : targetId;
    };

    const metricLabel = (targetId: string) => {
        const metric = metrics.find((item) => item.type === targetId);
        return metric ? `${metric.title} (${metric.type})` : targetId;
    };

    const handleSave = async () => {
        const trimmedCode = code.trim();
        if (!trimmedCode) {
            alert(t('disclaimers.enterCode'));
            return;
        }
        const duplicate = existing.some(
            (item) => item.code === trimmedCode && item.id !== disclaimer?.id,
        );
        if (duplicate) {
            alert(t('disclaimers.duplicateCode', { code: trimmedCode }));
            return;
        }
        if (!body.trim()) {
            alert(t('disclaimers.enterBody'));
            return;
        }
        if (requireAcknowledgement && !acknowledgementLabel.trim()) {
            alert(t('disclaimers.enterAcknowledgementLabel'));
            return;
        }

        let data: Record<string, unknown> = {};
        try {
            data = dataJson.trim() ? JSON.parse(dataJson) : {};
            if (typeof data !== 'object' || data === null || Array.isArray(data)) {
                throw new Error('data must be object');
            }
        } catch {
            alert(t('disclaimers.invalidData'));
            return;
        }

        const otherTargets = (disclaimer?.targets ?? []).filter(
            (target) => target.type !== 'test_configuration' && target.type !== 'test_metric',
        );
        const targets = uniqueTargets([
            ...otherTargets,
            ...configTargetIds.map(
                (targetId): DisclaimerTarget => ({ type: 'test_configuration', targetId }),
            ),
            ...metricTargetIds.map(
                (targetId): DisclaimerTarget => ({ type: 'test_metric', targetId }),
            ),
        ]);

        const dto: CreateDisclaimerDto = {
            code: trimmedCode,
            status,
            placement,
            severity,
            requireAcknowledgement,
            acknowledgementLabel: requireAcknowledgement ? acknowledgementLabel.trim() : null,
            title: titleField.trim() ? titleField.trim() : null,
            body: body.trim(),
            data,
            targets,
        };

        setSaving(true);
        try {
            if (isEdit && disclaimer) {
                await updateMutation.mutateAsync({ id: disclaimer.id, dto });
            } else {
                await createMutation.mutateAsync(dto);
            }
            onClose();
        } catch (e) {
            console.error(e);
            alert(apiErrorText(e, t('disclaimers.saveFailed')));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open fullWidth maxWidth="md" onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label={t('disclaimers.fieldCode')}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={isEdit}
                    />
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                        <TextField
                            select
                            label={t('disclaimers.fieldStatus')}
                            value={status}
                            onChange={(e) => setStatus(e.target.value as DisclaimerStatus)}
                            size="small"
                        >
                            {STATUSES.map((value) => (
                                <MenuItem key={value} value={value}>
                                    {t(`disclaimers.status.${value}`)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label={t('disclaimers.fieldPlacement')}
                            value={placement}
                            onChange={(e) => setPlacement(e.target.value as DisclaimerPlacement)}
                            size="small"
                        >
                            {PLACEMENTS.map((value) => (
                                <MenuItem key={value} value={value}>
                                    {t(`disclaimers.placement.${value}`)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label={t('disclaimers.fieldSeverity')}
                            value={severity}
                            onChange={(e) => setSeverity(e.target.value as DisclaimerSeverity)}
                            size="small"
                        >
                            {SEVERITIES.map((value) => (
                                <MenuItem key={value} value={value}>
                                    {t(`disclaimers.severity.${value}`)}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={requireAcknowledgement}
                                onChange={(e) => setRequireAcknowledgement(e.target.checked)}
                            />
                        }
                        label={t('disclaimers.fieldRequireAcknowledgement')}
                    />
                    <TextField
                        label={t('disclaimers.fieldAcknowledgementLabel')}
                        value={acknowledgementLabel}
                        onChange={(e) => setAcknowledgementLabel(e.target.value)}
                        size="small"
                        fullWidth
                        disabled={!requireAcknowledgement}
                    />
                    <TextField
                        label={t('disclaimers.fieldTitle')}
                        value={titleField}
                        onChange={(e) => setTitleField(e.target.value)}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label={t('disclaimers.fieldBody')}
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        multiline
                        minRows={4}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label={t('disclaimers.fieldData')}
                        value={dataJson}
                        onChange={(e) => setDataJson(e.target.value)}
                        multiline
                        minRows={3}
                        size="small"
                        fullWidth
                        placeholder="{}"
                    />
                    <FormControl size="small" fullWidth>
                        <InputLabel>{t('disclaimers.fieldConfigurations')}</InputLabel>
                        <Select
                            multiple
                            label={t('disclaimers.fieldConfigurations')}
                            value={configTargetIds}
                            onChange={(e) =>
                                setConfigTargetIds(
                                    typeof e.target.value === 'string' ? [] : e.target.value,
                                )
                            }
                            input={<OutlinedInput label={t('disclaimers.fieldConfigurations')} />}
                            renderValue={(selected) => selected.map(configLabel).join(', ')}
                        >
                            {sortedConfigs.map((config) => {
                                const targetId = String(config.id);
                                return (
                                    <MenuItem key={config.id} value={targetId}>
                                        <Checkbox checked={configTargetIds.includes(targetId)} />
                                        <ListItemText primary={`${config.id} ${config.name}`} />
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                        <InputLabel>{t('disclaimers.fieldMetrics')}</InputLabel>
                        <Select
                            multiple
                            label={t('disclaimers.fieldMetrics')}
                            value={metricTargetIds}
                            onChange={(e) =>
                                setMetricTargetIds(
                                    typeof e.target.value === 'string' ? [] : e.target.value,
                                )
                            }
                            input={<OutlinedInput label={t('disclaimers.fieldMetrics')} />}
                            renderValue={(selected) => selected.map(metricLabel).join(', ')}
                        >
                            {sortedMetrics.map((metric) => (
                                <MenuItem key={metric.id} value={metric.type}>
                                    <Checkbox checked={metricTargetIds.includes(metric.type)} />
                                    <ListItemText
                                        primary={metric.title}
                                        secondary={metric.type}
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DashboardDisclaimers;
